import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scissors } from "lucide-react";
import SEO from "@/components/storefront/SEO";
import { BRAND } from "@/lib/brand";

const PRECUTS = [
  ["Fat Quarter", "18\" × 22\"", "A quarter yard cut wide — not skinny. The most versatile precut for patchwork, pouches, and EPP.", "≈ 12 × 5\" charms or 99 sq in usable"],
  ["Fat Eighth", "9\" × 22\"", "Half a fat quarter. Great for scrappy quilts and small accents without over-buying.", "≈ 6 × 5\" charms"],
  ["Charm Pack", "5\" × 5\" squares", "Typically 40–42 coordinated squares from one collection. Zero cutting — straight to piecing.", "Baby quilt ≈ 2 packs"],
  ["Mini Charm", "2.5\" × 2.5\" squares", "The charm pack's little sibling. Perfect for postage-stamp quilts and tiny piecing.", "42 squares ≈ one mini quilt block set"],
  ["Jelly Roll", "2.5\" × 42–44\" strips", "Usually 40 strips, width of fabric. The strip-quilt classic: race quilts, log cabins, bindings.", "1 roll ≈ 2.75 yards"],
  ["Layer Cake", "10\" × 10\" squares", "Typically 42 large squares — big enough to subcut into half-square triangles or keep whole.", "1 cake ≈ 2.9 yards"],
  ["Honey Bun", "1.5\" × 42–44\" strips", "Narrow strips for fine piecing, string quilts, and sashing.", "1 bun ≈ 1.6 yards"],
  ["Half Yard", "18\" × 42–44\"", "The step up from a fat quarter when a project needs more of one print.", "2 fat quarters side by side"],
  ["Yard", "36\" × 42–44\"", "The baseline of quilting math. Backings, borders, and bindings usually start here.", "36\" of usable length"],
];

export default function PrecutGuide() {
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-14 md:py-20" data-testid="precut-guide-page">
      <SEO
        title="Precut Fabric Size Guide"
        description="Quilting precut sizes explained: fat quarter 18x22, charm pack 5x5, jelly roll 2.5in strips, layer cake 10x10, fat eighth, honey bun — with yields and project tips."
      />
      <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Maker resources</div>
      <h1 className="font-display text-4xl leading-tight md:text-[64px]">
        Precut <span className="font-display-soft italic">size guide</span>
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#3A2E33]">
        Precut names can feel like a secret language — jelly rolls, charm packs, honey buns. Here's the whole vocabulary
        in one table: exact dimensions, what each cut is best at, and roughly what you get out of it. Bookmark it; every
        quilter checks this twice a year.
      </p>

      <div className="mt-10 overflow-x-auto rounded-md border border-[#E6D4CC] bg-white">
        <table className="w-full text-left text-[14px]">
          <thead>
            <tr className="border-b border-[#E6D4CC] bg-[#F9F1EB] text-[11px] uppercase tracking-[0.18em] text-[#6B5158]">
              <th className="px-4 py-3">Precut</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3">Best for</th>
              <th className="px-4 py-3">Good to know</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6D4CC]">
            {PRECUTS.map(([name, size, best, note]) => (
              <tr key={name} className="align-top hover:bg-[#FAF6F1]" data-testid={`precut-row-${name.toLowerCase().replace(/ /g, "-")}`}>
                <td className="px-4 py-3.5 font-semibold text-[#2B2127]">{name}</td>
                <td className="whitespace-nowrap px-4 py-3.5 font-mono text-[13px] text-[#6D2E4B]">{size}</td>
                <td className="px-4 py-3.5 leading-relaxed text-[#3A2E33]">{best}</td>
                <td className="px-4 py-3.5 leading-relaxed text-[#6B5158]">{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 rounded-md border border-[#E6D4CC] bg-[#F1E3DC] p-6">
        <div className="flex items-start gap-3">
          <Scissors size={18} className="mt-0.5 flex-shrink-0 text-[#6D2E4B]" />
          <div className="text-[14px] leading-relaxed text-[#3A2E33]">
            <span className="font-semibold">A note on "usable width".</span> Quilting cotton is milled 42–44" wide, but
            after selvages and shrinkage most patterns assume <span className="font-semibold">40–42" of usable width
            (WOF)</span>. When math is tight, plan on 40" and let the extra be a bonus.
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Link to="/guides/quilt-calculators" className="inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-6 py-3 text-sm text-white hover:bg-[#59253d]" data-testid="to-calculators">
          Try the quilt calculators <ArrowRight size={14} />
        </Link>
        <Link to="/c/fabric" className="text-sm text-[#2B2127] underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#9D5C6C]">
          Shop fabric & precuts
        </Link>
      </div>
      <p className="mt-6 text-[12px] text-[#6B5158]">Curated by the {BRAND.name} team — makers first, shopkeepers second.</p>
    </div>
  );
}
